import * as React from 'react';

import {
  enumServiceTypeSortOption,
  ServiceTypeSortOption,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Badge,
  Content,
  DataGrid,
  DataGridColumn,
  Footer,
  HeaderIcons,
  LocalizedDate,
  Pagination,
  SearchField,
  Toolbar,
  type PaginationProps,
} from '@rematter/ui-web';

import { ServiceTypeDialog } from './ServiceTypeDialog';

import { useServiceTypesListQuery } from '~/modules/transportation/hooks/queries/useServiceTypesListQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { ServiceTypeRecord } from '~/modules/transportation/types';
import useDebouncedValue from '~/shared/hooks/useDebounce';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: ServiceTypeSortOption; desc: ServiceTypeSortOption }
> = {
  name: {
    asc: enumServiceTypeSortOption.NameAsc,
    desc: enumServiceTypeSortOption.NameDesc,
  },
  createdAt: {
    asc: enumServiceTypeSortOption.CreatedAtAsc,
    desc: enumServiceTypeSortOption.CreatedAtDesc,
  },
};

export const ServiceTypesListView: React.FC = () => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [offset, setOffset] = React.useState<number>(0);
  const [limit, setLimit] = React.useState<number>(50);
  const [sorting, setSorting] = React.useState<ServiceTypeSortOption[]>([
    enumServiceTypeSortOption.NameAsc,
  ]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editServiceType, setEditServiceType] =
    React.useState<ServiceTypeRecord | null>(null);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const { data, error, isLoading, isFetching, refetch } =
    useServiceTypesListQuery({
      filter: debouncedSearchTerm ? { searchTerm: debouncedSearchTerm } : {},
      sorting,
      pagination: { limit, offset },
    });

  const serviceTypes: ServiceTypeRecord[] = React.useMemo(
    () => data?.serviceTypes ?? [],
    [data?.serviceTypes],
  );
  const total = data?.total ?? 0;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleEdit = React.useCallback((serviceType: ServiceTypeRecord) => {
    setEditServiceType(serviceType);
    setIsDialogOpen(true);
  }, []);

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setSorting([enumServiceTypeSortOption.NameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [],
  );

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: ServiceTypeRecord }) => {
      handleEdit(row);
    },
    [handleEdit],
  );

  const columns: DataGridColumn<ServiceTypeRecord>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.serviceTypes.table.columns.name,
        width: 200,
        icon: HeaderIcons.Text,
        sortable: true,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center gap-2 font-medium">
            {row.name}
            {row.isSystemDefault && (
              <Badge color="neutral" size="small">
                {t.serviceTypes.table.systemDefault}
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: 'description',
        header: t.serviceTypes.table.columns.description,
        grow: 1,
        icon: HeaderIcons.Text,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="truncate text-neutral/70">
            {row.description ?? '-'}
          </div>
        ),
      },
      {
        key: 'isActive',
        header: t.serviceTypes.table.columns.status,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge color={row.isActive ? 'success' : 'neutral'} size="small">
            {row.isActive
              ? t.serviceTypes.table.active
              : t.serviceTypes.table.inactive}
          </Badge>
        ),
      },
      {
        key: 'createdAt',
        header: t.serviceTypes.table.columns.createdAt,
        sortable: true,
        width: 140,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.serviceTypes.emptyState.title,
      description: <span>{t.serviceTypes.emptyState.description}</span>,
      centered: true,
    }),
    [t],
  );

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.serviceTypes.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  return (
    <TransportationRootLayout className="@ServiceTypesListView">
      <Toolbar className="flex justify-between">
        <SearchField
          value={searchTerm}
          onChange={handleSearch}
          placeholder={t.serviceTypes.toolbar.search}
          className="w-[200px] shrink-0"
        />
        <div className="ml-auto text-sm text-neutral/60">
          {t.serviceTypes.predefinedNote}
        </div>
      </Toolbar>

      <Content className="h-full">
        <DataGrid<ServiceTypeRecord>
          isLoading={isLoading || isFetching}
          columns={columns}
          dataSource={serviceTypes}
          rowKey="id"
          aria-label={t.serviceTypes.table.aria}
          onColumnSort={handleColumnSort}
          columnVisibilityState={columnVisibilityState}
          className="w-full text-sm"
          fullWidth
          stickyHeader
          emptyPanelProps={emptyPanelProps}
        />
      </Content>

      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}

      <ServiceTypeDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditServiceType(null);
        }}
        onSuccess={() => {
          setIsDialogOpen(false);
          setEditServiceType(null);
          refetch();
        }}
        serviceType={editServiceType ?? undefined}
      />
    </TransportationRootLayout>
  );
};
